import { useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  AccessEvent,
  AccessEventsDateRange,
  AccessEventsFilter,
  MobileOpCode,
  Query,
  QueryGetAccessEventsForTenantArgs
} from 'graphql/generated/graphqlTypes';
import { GET_ACCESS_EVENTS_FOR_TENANT } from 'graphql/v2/queries';
import { useEffect, useRef, useState } from 'react';
import { TranslatedHookProps } from 'v2/hooks/hooks.types';

dayjs.extend(utc);

export const PAGE_SIZE = 100;

const getDateRange = (dateRange: AccessEventsDateRange) => ({
  rangeStart: dayjs(dateRange.rangeStart).second(0).millisecond(0).utc().format(),
  rangeEnd: dayjs(dateRange.rangeEnd).second(0).millisecond(0).subtract(1, 'millisecond').utc().format()
});

interface Props extends TranslatedHookProps {
  page: number;
  filter: AccessEventsFilter | null;
}

const useAccessEvents = ({ handleFetchError, translation, page, filter }: Props) => {
  const [accessEvents, setAccessEvents] = useState<AccessEvent[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);

  const pageTokenMapRef = useRef<Record<number, string | null>>({ 0: null });
  const currentPageRef = useRef(page);
  currentPageRef.current = page;
  const filterRef = useRef(filter);

  const [getAccessEvents, { loading }] = useLazyQuery<
    Pick<Query, 'getAccessEventsForTenant'>,
    QueryGetAccessEventsForTenantArgs
  >(GET_ACCESS_EVENTS_FOR_TENANT);

  const buildVariables = (token?: string | null) => ({
    nextToken: token ?? undefined,
    filter: {
      ...filter,
      actions: filter?.actions ?? [MobileOpCode.AccessDenied, MobileOpCode.AccessGranted],
      dateRange: filter?.dateRange ? getDateRange(filter.dateRange) : undefined
    }
  });

  useEffect(() => {
    if (filterRef.current !== filter) {
      filterRef.current = filter;
      pageTokenMapRef.current = { 0: null };
    }

    const token = pageTokenMapRef.current[page];
    if (token === undefined) return;

    (async () => {
      const { data, error } = await getAccessEvents({ variables: buildVariables(token) });
      if (error) {
        handleFetchError(translation.access_events_error_fetching);
        setHasNextPage(false);
        return;
      }
      if (data) {
        const { items, nextToken } = data.getAccessEventsForTenant;
        setAccessEvents(items);
        if (nextToken) {
          pageTokenMapRef.current[currentPageRef.current + 1] = nextToken;
          setHasNextPage(true);
        } else {
          setHasNextPage(false);
        }
      }
    })();
  }, [filter, page]);

  const rowCount = hasNextPage ? -1 : page * PAGE_SIZE + accessEvents.length;

  return {
    accessEvents,
    rowCount,
    loading
  };
};

export default useAccessEvents;
